import React, { CSSProperties } from 'react';
import { Box, BoxProps, forwardRef } from '@chakra-ui/react';
import {
  ColorMode,
  useColorMode,
} from '@allergan-data-labs/alle-elements-core';
import { getCounterBadgeStyles } from './counter-badge.theme';

export type CounterBadgeSize = 'sm' | 'lg';

export interface CounterBadgeProps {
  /**
   * Inline CSS styles
   */
  css?: CSSProperties;
  /**
   * The color of the CounterBadge.
   * @default light
   */
  colorMode?: ColorMode;
  /**
   * The size of the CounterBadge.
   * @default lg
   */
  size?: CounterBadgeSize;
  /**
   * The number to be displayed in the badge.
   * If "size" is "sm", the badge does not display a number.
   */
  count?: number;
  /**
   * The Chakra UI style props.
   */
  sx?: BoxProps['sx'];
}

/**
 * Counter badges show notifications or counts on navigation items or icons.
 */
export const CounterBadge = forwardRef<CounterBadgeProps, 'div'>(
  ({ css, colorMode: propColorMode, size = 'lg', count, ...rest }, ref) => {
    const { colorMode } = useColorMode(propColorMode);

    const isSmallSize = size === 'sm';
    const displayCount = isSmallSize
      ? ''
      : count && count > 99
      ? '99+'
      : count && count.toString();
    const ariaLabel = isSmallSize
      ? 'New notification'
      : `Notification count: ${displayCount}`;

    return (
      <Box
        ref={ref}
        role="status"
        aria-live="polite"
        aria-label={ariaLabel}
        style={css}
        sx={getCounterBadgeStyles({ colorMode, count, size })}
        {...rest}
      >
        {displayCount}
      </Box>
    );
  }
);
